import { feedEndpointLabel } from '@/lib/infra/feedEndpointLabel';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useMarketStore } from '../../stores/marketStore';
import type { FeedLogEntry, FeedOwnership, FeedVenue } from '../../stores/marketStore';
import { useGameStatesStore } from '../../stores/gameStatesStore';
import { useGameStateTape } from '../../stores/gameStateTape';
import { coreGameStateConfig, useCoreGameStatesStore } from '@/services/coreGameStates';
import { primaryGameStateEnabled } from '@/services/primaryGameState';
import type { GameStateConnectionStatus } from '@/lib/gameState/transport';
import { feedSharingHost, hostLabel, hostOf } from '@/lib/gameState/feedTape';

interface FeedTapeProps {
  onClose: () => void;
}

/** The tape repaints on its OWN clock, never on the store's flush. Bound to
 *  `version` (≤ 30 Hz) it re-rendered every row on every flush, and during an
 *  NFL burst that alone put a trader's tab seconds behind the feed (bugfix
 *  261012, confirmed: panel closed → lag 330 → 64 ms). 4 Hz is plenty for eyes. */
export const FEED_TAPE_TICK_MS = 250;
/** Rows drawn per column. The header still counts the whole log (last 100). */
export const FEED_TAPE_ROWS = 30;

// One formatter for the module: toLocaleTimeString builds an Intl formatter
// per call, and the tape formats a timestamp per row.
const TIME_FMT = new Intl.DateTimeFormat('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });

function formatTime(ts: number): string {
  const d = new Date(ts);
  return TIME_FMT.format(d) + '.' + String(d.getMilliseconds()).padStart(3, '0');
}

/** A row's React key: the log entry's identity. Log entries are pushed once
 *  and never mutated, so a row keeps its DOM node (and skips its render) while
 *  newer rows land above it; keyed by position, every new entry re-rendered
 *  every row beneath it. */
const rowIds = new WeakMap<FeedLogEntry, number>();
let nextRowId = 0;
function rowKey(entry: FeedLogEntry): number {
  let id = rowIds.get(entry);
  if (id === undefined) {
    id = ++nextRowId;
    rowIds.set(entry, id);
  }
  return id;
}

/** A game-state row named by its lane's engine — the same object while the
 *  label holds, so its key and its memoized row survive the next tick. */
const labelledRows = new WeakMap<FeedLogEntry, FeedLogEntry>();
function withFeedLabel(entry: FeedLogEntry, feedLabel: string | undefined): FeedLogEntry {
  const hit = labelledRows.get(entry);
  if (hit && hit.feedLabel === feedLabel) return hit;
  const next = { ...entry, feedLabel };
  labelledRows.set(entry, next);
  return next;
}

function shortTicker(ticker: string): string {
  // Canonical game keys (unabated/oddspapi rows): show the team pair.
  if (ticker.includes('|')) {
    const segs = ticker.split('|');
    return segs.slice(-2).join('-');
  }
  if (ticker.startsWith('PM-')) {
    const parts = ticker.split('-');
    return parts.slice(1, 4).join('-');
  }
  // Poly US slug `aec-<sport>-<away>-<home>-<yyyy-mm-dd>[-…]`: the sport has
  // its own column, so keep the matchup and any suffix past the date.
  if (ticker.startsWith('PMUS-')) {
    const parts = ticker.slice(5).replace(/-\d{4}-\d{2}-\d{2}(?=-|$)/, '').split('-');
    return (parts[0] === 'aec' ? parts.slice(2) : parts).join('-') || ticker;
  }
  const parts = ticker.split('-');
  return parts[parts.length - 1];
}

/** Display name for a feed: the engine identity declared in config (firehose
 *  'OG'; cores/edges via the `label@` prefix in VITE_HYDRA_CORE_FEEDS /
 *  VITE_HYDRA_EDGE_FEEDS — e.g. `hc1`, `he2`).
 *
 *  Fallback for an unlabeled feed is a POSITIONAL SLOT, and is deliberately
 *  spelled so it cannot be mistaken for an instance identity: `he-slot-1`,
 *  `hc-slot-2`, numbered by env-config order from the feed ids `edge:0`,
 *  `core:1`, ….
 *
 *  It must not read as `he1`/`hc1`. Rule 9 reserves that syntax for actual
 *  fleet instances, and this chip is a PROVENANCE surface — the URL behind it
 *  is only visible on hover, so a canonical-looking name here would let an
 *  unverified slot impersonate the real he1 to anyone glancing at the tape.
 *  That is precisely the ambiguity the `label@` mechanism exists to remove, so
 *  the fix is to label the env entry, and the fallback's job is to make its
 *  own ignorance obvious until someone does.
 *
 *  A bare `e<n>` is likewise never generated: that spelling marks the MVP
 *  proof-of-concept generation, which only the deployed e1 is entitled to, and
 *  only via its own env label. */
function feedLabel(feed: FeedOwnership): string {
  if (feed.label) return feed.label;
  if (feed.role === 'firehose') return 'OG';
  if (feed.opOnly === true) return 'op-slot'; // dedicated OP transport — label it in env
  const prefix = feed.role === 'core' ? 'hc' : 'he';
  const idx = Number(feed.id.split(':')[1]);
  return Number.isFinite(idx) ? `${prefix}-slot-${idx + 1}` : `${prefix}-slot-?`;
}

function TypeBadge({ type }: { type: string }) {
  const styles =
    type === 'market' ? 'bg-green-900/40 text-green-400' :
    type === 'consensus' ? 'bg-amber-900/40 text-amber-300' :
    type === 'opOdds' ? 'bg-emerald-900/40 text-emerald-300' :
    type === 'gameState' ? 'bg-sky-900/40 text-sky-300' :
    'bg-yellow-900/40 text-yellow-400';
  const letter =
    type === 'market' ? 'M' :
    type === 'consensus' ? 'C' :
    type === 'opOdds' ? 'OP' :
    type === 'gameState' ? 'GS' :
    'B';
  return (
    <span className={`inline-flex min-w-4 items-center justify-center rounded px-0.5 text-[9px] ${styles}`}>
      {letter}
    </span>
  );
}

// Consensus values render at tenth-of-a-cent grain when the frame carried
// tenths (hydra ≥2026-07-05) — frames emit on tenth-only moves, so showing
// the integer would make consecutive rows look like duplicate no-ops.
// Falls back to the integer cents on pre-rollout frames. Soccer 3-way
// frames append the draw; the ˢ marks Pinnacle-anchored computes (a
// sharp flip alone emits a frame with zero numeric change).
function ConsensusValue({ entry }: { entry: FeedLogEntry }) {
  const fmt = (tenths: number | undefined, cents: number | undefined) =>
    tenths != null ? (tenths / 10).toFixed(1) : String(cents);
  return (
    <span className="text-foreground/90 shrink-0 tabular-nums">
      {fmt(entry.awayProbTenths, entry.awayProbCents)}
      <span className="text-muted-foreground">/</span>
      {fmt(entry.homeProbTenths, entry.homeProbCents)}
      {entry.drawProbTenths != null && (
        <>
          <span className="text-muted-foreground">/</span>
          {(entry.drawProbTenths / 10).toFixed(1)}
        </>
      )}
      {entry.sharpBookUsed === true && (
        <span className="text-amber-300" title="Pinnacle-anchored (sharpBookUsed)">
          ˢ
        </span>
      )}
    </span>
  );
}

/** Source column: WHICH engine delivered this row (OG / e2 / hc1 …). The
 *  provenance is the point of the tape — it outranks the values, which
 *  truncate first when the row gets tight. */
function SourceTag({ label }: { label?: string }) {
  return (
    <span className="text-foreground/60 bg-muted/40 w-8 shrink-0 truncate rounded px-0.5 text-center text-[8px] font-bold uppercase">
      {label ?? '—'}
    </span>
  );
}

/** Game-state row: `away-home` score in the record's own away/home order, then
 *  the status (period + clock while live), then the Core lane's provider. */
function GameStateValue({ entry }: { entry: FeedLogEntry }) {
  const status =
    entry.gsStatusId === 3 ? 'FINAL' :
    entry.gsStatusId === 1 ? 'PRE' :
    [entry.gsPeriodLabel, entry.gsClock].filter(Boolean).join(' ') || 'LIVE';
  return (
    <>
      {entry.gsStatusId !== 1 && (
        <span className="text-foreground/90 shrink-0 tabular-nums" title="away-home">
          {entry.awayScore ?? '—'}<span className="text-muted-foreground">-</span>{entry.homeScore ?? '—'}
        </span>
      )}
      <span className="text-muted-foreground max-w-20 shrink-0 truncate">{status}</span>
      {entry.gsProvider && (
        <span className="text-sky-300/70 max-w-16 shrink-0 truncate" title={`provider: ${entry.gsProvider}`}>
          {entry.gsProvider}
        </span>
      )}
    </>
  );
}

function FeedRowBody({ entry }: { entry: FeedLogEntry }) {
  // Game-state summary row: games a lane started reporting in one response.
  if (entry.type === 'gameState' && entry.gsNewGames != null) {
    return (
      <div className="border-border/40 hover:bg-muted/40 flex items-center gap-1.5 border-b px-2 py-0.5 font-mono text-[10px]">
        <span className="text-muted-foreground w-[70px] shrink-0">{formatTime(entry.ts)}</span>
        <TypeBadge type={entry.type} />
        <SourceTag label={entry.feedLabel} />
        <span className="text-foreground/80 min-w-0 flex-1 truncate">
          +{entry.gsNewGames} {entry.gsNewGames === 1 ? 'game' : 'games'}
        </span>
      </div>
    );
  }
  // OP snapshot-commit summary row (one per effective commit).
  if (entry.type === 'opOdds' && entry.opSnapshotGames != null) {
    return (
      <div className="border-border/40 hover:bg-muted/40 flex items-center gap-1.5 border-b px-2 py-0.5 font-mono text-[10px]">
        <span className="text-muted-foreground w-[70px] shrink-0">{formatTime(entry.ts)}</span>
        <TypeBadge type={entry.type} />
        <SourceTag label={entry.feedLabel} />
        <span className="text-foreground/80 min-w-0 flex-1 truncate">
          snapshot · {entry.opSnapshotGames} games
        </span>
      </div>
    );
  }
  return (
    <div className="border-border/40 hover:bg-muted/40 flex items-center gap-1.5 border-b px-2 py-0.5 font-mono text-[10px]">
      <span className="text-muted-foreground w-[70px] shrink-0">{formatTime(entry.ts)}</span>
      <TypeBadge type={entry.type} />
      <SourceTag label={entry.feedLabel} />
      <span className="text-muted-foreground w-6 shrink-0 text-center uppercase">{entry.sport}</span>
      <span className="text-foreground/80 min-w-0 flex-1 truncate">{entry.gsMatchup ?? shortTicker(entry.ticker)}</span>
      {entry.type === 'opOdds' && entry.opTombstone === true && (
        <span className="shrink-0 text-red-400" title="no-coverage tombstone (markets: [])">∅</span>
      )}
      {entry.type === 'opOdds' && !entry.opTombstone && entry.awayProbCents != null && entry.homeProbCents != null && (
        <ConsensusValue entry={entry} />
      )}
      {entry.type === 'opOdds' && !entry.opTombstone && entry.bookCount != null && (
        <span className="text-muted-foreground shrink-0">{entry.bookCount}bk</span>
      )}
      {entry.yesBid != null && (
        <span className="text-foreground/90 shrink-0 tabular-nums">
          {entry.yesBid}<span className="text-muted-foreground">/</span>{entry.yesAsk ?? '—'}
        </span>
      )}
      {entry.type === 'market' && (
        <span
          className={`shrink-0 tabular-nums ${entry.volume != null && entry.volume > 0 ? 'text-cyan-400' : 'text-muted-foreground'}`}
          title="Frame's cumulative lifetime traded volume, not rolling 24-hour volume"
        >
          {entry.volume != null ? `v${entry.volume}` : 'v?'}
        </span>
      )}
      {entry.type === 'orderbook' && entry.bookLevels != null && (
        <span className="text-muted-foreground shrink-0">{entry.bookLevels}L</span>
      )}
      {entry.type === 'consensus' && entry.awayProbCents != null && entry.homeProbCents != null && (
        <ConsensusValue entry={entry} />
      )}
      {entry.type === 'gameState' && <GameStateValue entry={entry} />}
    </div>
  );
}

/** Entries are immutable, so a row whose entry is unchanged never re-renders. */
const FeedRow = memo(FeedRowBody);

/** One server's chip: `OG MLB SOCCER …` / `c1 CRICKET` / `e2 NFL`. Live
 *  ownership — sports move between chips as the store's arbitration moves
 *  them (a claimant drop/block hands a sport back to OG, where OG carries
 *  it). A disconnected core/edge reads red; claimed-but-not-served sports
 *  render dim (on OG — or dark, for sports OG never carried — until the
 *  engine announces them). Chip color groups the fleet: OG blue, cores
 *  violet, edges cyan. */
/** Venues an instance's NAME says it runs — the rule-9 venue token in
 *  `he<venue>-<sport><main|alt>-<class><NN>`: `klpi` Kalshi + Poly INTL,
 *  `kl` Kalshi, `pi` Poly INTL, `pu` Poly US (always its own instance).
 *  null when the name makes no venue claim — OG, cores, the numbered
 *  `he<n>` fleet — and the chip goes wherever ownership puts it. */
const VENUE_TOKENS: Record<string, readonly FeedVenue[]> = {
  klpi: ['kalshi', 'polymarket'],
  kl: ['kalshi'],
  pi: ['polymarket'],
  pu: ['polymarket_us'],
};
function venuesNamedBy(label: string | undefined): readonly FeedVenue[] | null {
  const token = label?.match(/^he(klpi|kl|pi|pu)-/)?.[1];
  return token ? VENUE_TOKENS[token] : null;
}

/** One venue column's chips. Skips the dedicated OP transport (opOnly — no
 *  market data) and any instance whose name says it does not run this venue
 *  (`hepu-*` under Kalshi): all it could show there is idle claims. It stays
 *  if it somehow OWNS a sport here — that is real data, never hidden. A dark
 *  sport pinned on a skipped chip moves to the firehose entry, where an
 *  unclaimed dark sport already lives, so it never vanishes; with no
 *  firehose to carry it, the chip stays. */
function venueChips(feeds: readonly FeedOwnership[], venue: FeedVenue): FeedOwnership[] {
  const firehose = feeds.find((f) => f.role === 'firehose');
  const movedDark: FeedOwnership['darkClaims'] = [];
  const kept = feeds.filter((f) => {
    if (f.opOnly === true) return false;
    const named = venuesNamedBy(f.label);
    if (named == null || named.includes(venue) || f.sports.length > 0) return true;
    if (f.darkClaims.length > 0 && !firehose) return true;
    movedDark.push(...f.darkClaims);
    return false;
  });
  if (movedDark.length === 0) return kept;
  return kept.map((f) =>
    f === firehose ? { ...f, darkClaims: [...new Set([...f.darkClaims, ...movedDark])].sort() } : f);
}

/** OddsPapi column sort key: ladder rank when the feed carries one, else the
 *  pre-ladder rule — the designated primary ahead of everything else. */
function opSortKey(feed: FeedOwnership): number {
  if (feed.opRank != null) return feed.opRank;
  return feed.opPreferred === true ? -1 : Number.MAX_SAFE_INTEGER;
}

/** Odds-column channel tag: WHICH odds channel this engine is serving right
 *  now. The merged Odds column shows one chip per engine, so the channel
 *  (unabated consensus vs OddsPapi) moves onto the chip as a tag — `hc1
 *  [OPPI]`, `OG·2 [UNBTD]` — instead of being implied by which column the
 *  chip sits in. Tag colors keep each channel's former column color. */
type OddsChannelTag = 'UNBTD' | 'OPPI';
/** A game-state lane riding a chip. It carries its OWN health: the lane is a
 *  separate transport from the engine's market socket (a REST poll or the Core
 *  game-state socket), so the chip badge cannot speak for it. */
interface GameStateLaneTag {
  lane: 'poll' | 'core';
  url: string;
  state: 'live' | 'waiting' | 'down';
  title: string;
}
/** `opLadder` marks a chip in the Odds column, the only column where the OP
 *  ladder rank means anything. opRank is a property of the FEED, so every
 *  venue column receives it too; without this flag a core ranked behind
 *  another on OddsPapi read `hc1·2` under Kalshi, where it is still the
 *  primary source. */
type ChipFeed = FeedOwnership & { channelTags?: OddsChannelTag[]; gameStateLanes?: GameStateLaneTag[]; opLadder?: boolean };

function ServerChip({ feed }: { feed: ChipFeed }) {
  const down = feed.role !== 'firehose' && !feed.connected;
  const badge = down
    ? 'bg-red-900/40 text-red-400'
    : feed.role === 'core'
      ? 'bg-violet-900/40 text-violet-300'
      : feed.role === 'edge'
        ? 'bg-cyan-900/40 text-cyan-300'
        : 'bg-blue-900/40 text-blue-300';
  // Dim ONLY a healthy standby that serves NOTHING in this column — a downed
  // chip stays full-opacity red (a dimmed red chip was nearly invisible,
  // which is the opposite of what a dead primary should look like), and an
  // engine actively serving a channel (OG producing unabated while standing
  // by on the OP ladder) must not read as idle.
  const serving = (feed.channelTags?.length ?? 0) + (feed.gameStateLanes?.length ?? 0) > 0;
  const dim = feed.opStandby === true && !down && !serving;
  // Rank marker on ladder members: the OddsPapi column is a preference order
  // (hc1, hc2, … OG last), and without it two violet core chips are
  // indistinguishable — which one is g1 actually consuming, and which is the
  // backup waiting behind it? Odds column only (opLadder): a venue column's
  // chips are not ranked.
  const rank = feed.opLadder === true ? feed.opRank : undefined;
  const rankMark = rank != null && rank > 0 ? `·${rank + 1}` : '';
  // The transport and host identify the upstream without exposing connection
  // credentials from userinfo, paths, query parameters or fragments.
  const rankNote = rank != null ? ` (rank ${rank + 1})` : '';
  const endpoint = feed.url ? feedEndpointLabel(feed.url) : null;
  const title = down
    ? `${feedLabel(feed)}${rankNote} disconnected${endpoint ? ` — ${endpoint}` : ''}`
    : feed.opStandby === true
      ? `${feedLabel(feed)}${rankNote} standby${endpoint ? ` — ${endpoint}` : ''}`
      : rank != null
        ? `${feedLabel(feed)}${rankNote} live${endpoint ? ` — ${endpoint}` : ''}`
        : endpoint ?? undefined;
  return (
    <span
      className={`flex min-w-0 items-center gap-1 ${dim ? 'opacity-50' : ''}`}
      title={title}
    >
      <span className={`shrink-0 rounded px-1 text-[9px] font-bold ${badge}`}>
        {feedLabel(feed)}{rankMark}
      </span>
      {feed.channelTags?.map((tag) => (
        <span
          key={tag}
          className={`shrink-0 rounded px-0.5 text-[8px] font-bold ${
            tag === 'OPPI' ? 'bg-emerald-900/40 text-emerald-300' : 'bg-amber-900/40 text-amber-300'
          }`}
          title={`${feedLabel(feed)} is serving the ${tag === 'OPPI' ? 'OddsPapi' : 'Unabated'} channel`}
        >
          {tag}
        </span>
      ))}
      {feed.gameStateLanes?.map((gs) => (
        <span
          key={gs.lane}
          className={`shrink-0 rounded px-0.5 text-[8px] font-bold ${
            gs.state === 'down' ? 'bg-red-900/40 text-red-400' : 'bg-sky-900/40 text-sky-300'
          } ${gs.state === 'waiting' ? 'opacity-50' : ''}`}
          title={gs.title}
        >
          GS
        </span>
      ))}
      <span className="text-foreground/70 min-w-0 truncate text-[9px] uppercase">
        {feed.sports.join(' ')}
        {feed.floorSports.length > 0 && (
          // D10 FLOOR: this core is covering these sports with the THIN
          // firehose because no primary serves them — honest degraded
          // state (amber), distinct from real ownership (plain), idle
          // (muted) and dark (red struck).
          <span
            className="text-amber-300/80"
            title={`THIN FLOOR — no primary is serving ${feed.floorSports.join(', ').toUpperCase()}; ${feedLabel(feed)}'s throttled firehose is covering (rule 6). Reassign or restore a primary to clear.`}
            data-floor-sports
          >
            {feed.sports.length > 0 ? ' ' : ''}
            {feed.floorSports.join(' ')}
          </span>
        )}
        {feed.idleClaims.length > 0 && (
          <span className={`text-muted-foreground/60 ${down ? 'line-through' : ''}`}>
            {feed.sports.length > 0 ? ' ' : ''}{feed.idleClaims.join(' ')}
          </span>
        )}
        {feed.darkClaims.length > 0 && (
          // DARK: nobody in the fleet is serving these. Distinct from an idle
          // claim, which still flows from the firehose — here there is no
          // data at all, from anyone. Struck through in red so the tape stops
          // implying some engine has it: attributing a dark sport to OG sent
          // one debugging session at the wrong engine entirely.
          <span
            className="text-red-400/70 line-through"
            title={`no engine is serving ${feed.darkClaims.join(', ').toUpperCase()} — the claimant is not delivering it and OG does not carry it`}
          >
            {feed.sports.length > 0 || feed.idleClaims.length > 0 ? ' ' : ''}
            {feed.darkClaims.join(' ')}
          </span>
        )}
      </span>
    </span>
  );
}

/** The game-state lanes this build runs, with each one's health.
 *
 *  poll — the legacy /api/analytics/game-states poll (Unabated), off when the
 *         build reads Core as its primary game state.
 *  core — the Core game-state lane: the directory's `lanes.gamestate`, else
 *         VITE_HYDRA_GAMESTATE_URL. */
function gameStateLanes(
  pollError: string | null,
  pollAnswered: boolean,
  coreStatus: GameStateConnectionStatus,
  coreError: string | null,
): GameStateLaneTag[] {
  const lanes: GameStateLaneTag[] = [];
  const pollUrl = import.meta.env.VITE_HYDRA_ANALYTICS_URL as string | undefined;
  if (pollUrl && !primaryGameStateEnabled()) {
    const state = pollError != null ? 'down' : pollAnswered ? 'live' : 'waiting';
    const detail = state === 'down' ? `failing: ${pollError}` : state === 'waiting' ? 'no response yet' : 'live';
    lanes.push({ lane: 'poll', url: pollUrl, state, title: `Game state — Unabated poll, ${detail} — ${feedEndpointLabel(pollUrl)}` });
  }
  const coreUrl = coreGameStateConfig()?.baseUrl;
  if (coreUrl) {
    const state = coreStatus === 'live' ? 'live' : coreStatus === 'disconnected' ? 'down' : 'waiting';
    const detail = coreError != null ? `${coreStatus}: ${coreError}` : coreStatus;
    lanes.push({ lane: 'core', url: coreUrl, state, title: `Game state — Core lane, ${detail} — ${feedEndpointLabel(coreUrl)}` });
  }
  return lanes;
}

function FeedColumnBody({
  title,
  color,
  entries,
  servers,
}: {
  title: string;
  color: string;
  entries: FeedLogEntry[];
  servers: ChipFeed[];
}) {
  const marketCount = entries.filter(e => e.type === 'market').length;
  const bookCount = entries.filter(e => e.type === 'orderbook').length;
  const consensusCount = entries.filter(e => e.type === 'consensus').length;
  const opCount = entries.filter(e => e.type === 'opOdds').length;
  const gameStateCount = entries.filter(e => e.type === 'gameState').length;
  // Each column only emits its own types (Odds and Game State: consensus +
  // opOdds + gameState; venue columns: market+book) — show the relevant subset
  // so the header stays tight, every channel that is flowing.
  const channelCounts = [
    consensusCount > 0 ? `${consensusCount}C` : null,
    opCount > 0 ? `${opCount}OP` : null,
    gameStateCount > 0 ? `${gameStateCount}GS` : null,
  ].filter((part): part is string => part != null);
  const summary =
    marketCount === 0 && bookCount === 0 && channelCounts.length > 0
      ? channelCounts.join(' / ')
      : `${marketCount}M / ${bookCount}B`;

  return (
    <div className="flex flex-1 flex-col min-w-0">
      <div className="border-border border-b px-2 py-1.5">
        <div className="flex items-center justify-between">
          <span className={`text-[11px] font-bold ${color}`}>{title}</span>
          <span className="text-muted-foreground text-[10px]">{summary}</span>
        </div>
        {servers.length > 0 && (
          <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-0.5">
            {servers.map((feed) => <ServerChip key={feed.id} feed={feed} />)}
          </div>
        )}
      </div>
      <div className="max-h-72 overflow-y-auto">
        {entries.length === 0 ? (
          <div className="text-muted-foreground px-2 py-3 text-center text-[10px]">Waiting...</div>
        ) : (
          entries.slice(0, FEED_TAPE_ROWS).map((entry) => <FeedRow key={rowKey(entry)} entry={entry} />)
        )}
      </div>
    </div>
  );
}

/** Its inputs only change on a tape tick that saw new feed content. */
const FeedColumn = memo(FeedColumnBody);

interface TapeSnapshot {
  /** Store version this snapshot was read at; a tick at the same version reads nothing. */
  version: number;
  kalshi: FeedLogEntry[];
  poly: FeedLogEntry[];
  polyUs: FeedLogEntry[];
  unabated: FeedLogEntry[];
  op: FeedLogEntry[];
  kalshiServers: FeedOwnership[];
  kalshiChips: FeedOwnership[];
  polyChips: FeedOwnership[];
  polyUsChips: FeedOwnership[];
}

// Live sport ownership (edge preferred / firehose fallback) for the server
// chips is resolved PER VENUE: within an announced sport the edge only owns a
// venue it is actually delivering (e.g. Kalshi toggled off edge-side moves
// MLB to OG in the Kalshi column while the Poly column keeps it on the edge).
function readTape(): TapeSnapshot {
  const st = useMarketStore.getState();
  const kalshiServers = st.getFeedOwnership('kalshi');
  return {
    version: st.version,
    kalshi: st.getFeedLog('kalshi'),
    poly: st.getFeedLog('polymarket'),
    polyUs: st.getFeedLog('polymarket_us'),
    unabated: st.getFeedLog('unabated'),
    op: st.getFeedLog('oddspapi'),
    kalshiServers,
    kalshiChips: venueChips(kalshiServers, 'kalshi'),
    polyChips: venueChips(st.getFeedOwnership('polymarket'), 'polymarket'),
    polyUsChips: venueChips(st.getFeedOwnership('polymarket_us'), 'polymarket_us'),
  };
}

/** The feed log and ownership, read every FEED_TAPE_TICK_MS while the tab is
 *  visible and the store has published since the last read. The reads happen
 *  here, off the render path: the getters are impure (module-level ring
 *  buffers), which is what made the old version-bound memos unsafe under the
 *  React Compiler (#200). */
function useTapeSnapshot(): TapeSnapshot {
  const [snap, setSnap] = useState(readTape);
  const shownVersion = useRef(snap.version);
  useEffect(() => {
    const refresh = () => {
      if (document.hidden) return;
      if (useMarketStore.getState().version === shownVersion.current) return;
      const next = readTape();
      shownVersion.current = next.version;
      setSnap(next);
    };
    const timer = setInterval(refresh, FEED_TAPE_TICK_MS);
    // Back from a hidden tab: catch up at once rather than on the next tick.
    document.addEventListener('visibilitychange', refresh);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, []);
  return snap;
}

export function FeedTape({ onClose }: FeedTapeProps) {
  const tape = useTapeSnapshot();
  const kalshiServers = tape.kalshiServers;

  // Game-state lane health, read as primitives (a fresh object per read would
  // re-render forever under zustand 5).
  const pollError = useGameStatesStore((s) => s.error);
  const pollAnswered = useGameStatesStore((s) => s.lastFetchMs > 0);
  const coreStatus = useCoreGameStatesStore((s) => s.status);
  const coreError = useCoreGameStatesStore((s) => s.error);
  const gameStateEntries = useGameStateTape((s) => s.entries);

  // Each game-state lane resolved to the engine it rides: the market feed
  // sharing its host (named as that feed's chip is), else its own host. Chips
  // and rows read the same name.
  const lanes = useMemo(() => gameStateLanes(pollError, pollAnswered, coreStatus, coreError).map((gs) => {
    const feed = feedSharingHost(gs.url, kalshiServers);
    return { gs, feed, label: feed ? feedLabel(feed) : hostLabel(gs.url) ?? 'gs-?' };
  }), [kalshiServers, pollError, pollAnswered, coreStatus, coreError]);

  // The Odds and Game State column carries BOTH odds channels — unabated
  // consensus and OddsPapi — plus the game-state lanes, as one stream, newest
  // first. Row badges (C / OP / GS) and the per-chip channel tags keep them
  // distinguishable inside the merge. Game-state rows arrive through their own
  // store, so they land at once, without waiting for a tape tick.
  const oddsEntries = useMemo(() => {
    const laneLabel = (lane: FeedLogEntry['gsLane']) => lanes.find((l) => l.gs.lane === lane)?.label;
    return [
      ...tape.unabated,
      ...tape.op,
      ...gameStateEntries.map((e) => withFeedLabel(e, laneLabel(e.gsLane))),
    ].sort((a, b) => b.ts - a.ts);
  }, [tape.unabated, tape.op, gameStateEntries, lanes]);

  // The Odds column pins ONE chip per engine involved in either odds channel,
  // with a channel tag for what each is serving RIGHT NOW — `hc1 [OPPI]`,
  // `OG·2 [UNBTD]`. The set is the OP ladder (every OP-capable feed, in
  // LADDER ORDER — hc1, hc2, … OG last — so "which core is live and which is
  // waiting behind it" stays answerable at a glance; a feed with no rank
  // falls back to designated-primary-first so the primary never reads as the
  // backup) merged with the unabated provider (OG — ungated, phasing out).
  // An engine on both channels gets one chip with both facts: OG keeps its
  // ladder rank mark while wearing UNBTD. Only a chip serving NOTHING dims
  // as standby; the ACTIVE OP source is the one wearing OPPI.
  //
  // Game-state lanes join the same set: a lane rides the chip of the engine
  // sharing its host (`hc1 [OPPI] [GS]`), or gets its own chip named by its
  // host (`sh0-hc [GS]`). The GS tag carries the lane's own health.
  const oddsServers = useMemo(() => {
    const byId = new Map<string, ChipFeed>();
    const ladder = kalshiServers
      .filter((f) => f.opAuthority === true)
      .map<ChipFeed>((f) => ({
        ...f,
        sports: [], idleClaims: [], darkClaims: [], floorSports: [],
        opStandby: f.opActive !== true,
        channelTags: f.opActive === true ? ['OPPI'] : [],
      }))
      .sort((a, b) => opSortKey(a) - opSortKey(b));
    for (const f of ladder) byId.set(f.id, f);
    // The UNBTD tag names the Unabated PROVIDER — the OG firehose. A firehose-
    // role feed that is a full OP source (the gateway) serves no such channel.
    const firehose = kalshiServers.find((f) => f.role === 'firehose' && f.opFullFeed !== true);
    if (firehose) {
      const existing = byId.get(firehose.id);
      if (existing) existing.channelTags = [...(existing.channelTags ?? []), 'UNBTD'];
      else byId.set(firehose.id, { ...firehose, sports: [], idleClaims: [], darkClaims: [], floorSports: [], channelTags: ['UNBTD'] });
    }
    for (const { gs, feed, label } of lanes) {
      const id = feed?.id ?? `gamestate:${hostOf(gs.url) ?? gs.lane}`;
      const chip: ChipFeed = byId.get(id) ?? (feed
        ? { ...feed, sports: [], idleClaims: [], darkClaims: [], floorSports: [] }
        : {
            id, role: 'core', connected: gs.state !== 'down', label, url: gs.url,
            sports: [], idleClaims: [], darkClaims: [], floorSports: [],
          });
      chip.gameStateLanes = [...(chip.gameStateLanes ?? []), gs];
      byId.set(id, chip);
    }
    return [...byId.values()].map((f) => ({ ...f, opLadder: true }));
  }, [kalshiServers, lanes]);

  return (
    <div className="bg-background/95 border-border absolute bottom-full right-0 z-50 mb-1 w-[1440px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border shadow-2xl">
      {/* Header */}
      <div className="border-border flex items-center justify-between border-b px-3 py-1.5">
        <span className="text-foreground text-xs font-semibold">Live Feed</span>
        <button
          type="button"
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground px-1 text-xs"
        >
          ✕
        </button>
      </div>

      {/* Live streams. Venue columns carry the instances that can run their
          venue (venueChips); the dedicated OP transport chip lives in the Odds
          and Game State column only. */}
      <div className="divide-border flex divide-x">
        <FeedColumn title="Kalshi" color="text-blue-400" entries={tape.kalshi} servers={tape.kalshiChips} />
        <FeedColumn title="Poly INTL" color="text-purple-400" entries={tape.poly} servers={tape.polyChips} />
        <FeedColumn title="Poly US" color="text-fuchsia-400" entries={tape.polyUs} servers={tape.polyUsChips} />
        <FeedColumn title="Odds and Game State" color="text-emerald-400" entries={oddsEntries} servers={oddsServers} />
      </div>
    </div>
  );
}
